import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { Formik, Form } from 'formik';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import { TOUR_SHOWING_STATUS_FAILED, TOUR_SHOWING_STATUS_COMPLETED } from 'common/tourConstants';

import {
  SR_API_ACTION_TOUR_FAILED_FEEDBACK,
  SR_API_ACTION_SELF_SHOWING_LOCK_REMOTE_UNLOCK,
} from 'api/actionTypes';

import { formatPhoneNumber } from 'common/formatters';
import { makeListingFullAddressString } from 'common/listingHelpers';

import { createFormValidationFn, createInitialValues } from 'client/helpers/formikHelpers';

import ListingActions from 'client/actions/Listing';

import useBranch from 'client/hooks/useBranch';
import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';
import safeLocalStorage from 'client/lib/safeLocalStorage';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_YELLOW,
} from 'client/components/SrButton/SrButton';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrSunlockStatus from 'client/components/SrSunlockStatus/SrSunlockStatus';

import './SrTourListingFailedModal.less';

const formFields = [
  {
    id: 'tourCompleted',
    title: '',
    subtitle: '',
    type: 'yes_no',
    validations: ['required'],
  },
];

const SrTourListingFailedModal = ({
  user,
  listing,
  sunlock,
  codeboxLock,
  tourListingId,
  onSubmit,
}) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  const initialButtonText = sunlock ? 'Unlock Door Now' : 'Generate One-Time Use Code';

  const loadingButtonText = sunlock ? 'Unlocking...' : 'Generating Code...';

  const [codeboxLockCode, setCodeboxLockCode] = useState(null);
  const [unlockButtonText, setUnlockButtonText] = useState(initialButtonText);
  const [unlockError, setUnlockError] = useState(null);

  const dispatch = useDispatch();

  const { sunlockHealth } = useBranch({
    sunlockHealth: ['sunlockHealth', listing.id],
  });

  useEffect(() => {
    if (sunlock && listing.id && !sunlockHealth) {
      dispatch(ListingActions.getSunlockHealth, {
        listingId: listing.id,
        forceRefresh: true,
      });
    }
  }, [listing.id, dispatch, sunlock, sunlockHealth]);

  const parseUnlockErrorMessage = (error) => {
    const errorShortId = error.details?.shortId || null;
    const errorMessage = error.message || 'Unknown error';

    return errorShortId
      ? `${errorMessage} Please contact support (Error ID: ${errorShortId})`
      : errorMessage;
  };

  const requestSelfShowingLockUnlock = async () => {
    setUnlockButtonText(loadingButtonText);
    await dispatch(postWithState, {
      action: SR_API_ACTION_SELF_SHOWING_LOCK_REMOTE_UNLOCK,
      payload: {
        listingId: listing.id,
        tourListingId,
      },
      onSuccess: ({ otpCode }) => {
        if (otpCode) {
          setCodeboxLockCode(otpCode || null);
        }
        if (sunlock) {
          setUnlockButtonText('Unlocked!');
        }
      },
      onError: (error) => {
        const errorMessage = parseUnlockErrorMessage(error);
        setUnlockButtonText('Failed to unlock door');
        setUnlockError(errorMessage);
      },
    });

    setTimeout(() => {
      setUnlockButtonText(initialButtonText);
      setUnlockError(null);
    }, 6000);
  };

  const onSubmitForm = (formValues) => {
    const tourListingStatus =
      formValues.tourCompleted === true
        ? TOUR_SHOWING_STATUS_COMPLETED
        : TOUR_SHOWING_STATUS_FAILED;

    safeLocalStorage.removeItem('sunlockBackupCodeShown');

    dispatch(postWithState, {
      action: SR_API_ACTION_TOUR_FAILED_FEEDBACK,
      payload: {
        tourListingId: tourListingId,
        tourListingStatus: tourListingStatus,
      },
      onSuccess: () => {
        onSubmit({ tourListingStatus });
      },
    });
  };

  return (
    <SrModalV2
      showCloseButton={false}
      closeOnBackgroundClick
      contentMaxWidth={!isSmallScreen ? '450px' : null}
      padding="0"
      theme={MODAL_THEME_STACK}
      zIndex="980"
    >
      <Formik
        initialValues={createInitialValues({ formFields })}
        validate={createFormValidationFn(formFields)}
        onSubmit={onSubmitForm}
        render={(formikBag) => {
          return (
            <Form>
              <SrBox
                styleName="propertyImage"
                backgroundImage={`url("${listing.sunroomPhotoUrls[0]}")`}
              />
              <div styleName="content">
                <SrText font="greycliff" weight="700" size="26" lineHeight="36px">
                  {listing.addressStreetDisplay}
                  <br />
                  {`${listing.addressCity}, ${listing.addressStateCode} ${listing.addressZip}`}
                </SrText>

                {sunlock && (
                  <SrBox margin="20px 0">
                    <SrFlex direction="col" align="center" styleName="sunlockCode">
                      <SrText font="greycliff" weight="700">
                        Admin Unlock
                      </SrText>
                      <SrBox padding="8px">
                        <SrButton
                          theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                          onClick={requestSelfShowingLockUnlock}
                          disabled={unlockButtonText !== initialButtonText}
                        >
                          {unlockButtonText}
                        </SrButton>
                      </SrBox>
                    </SrFlex>
                    {unlockError && (
                      <SrBox>
                        <SrErrorMessage>{unlockError}</SrErrorMessage>
                      </SrBox>
                    )}
                    {sunlockHealth && sunlock && (
                      <SrSunlockStatus
                        listingAddress={makeListingFullAddressString(listing)}
                        sunlockStatus={sunlockHealth.sunlockStatus?.status}
                        sunlockSerial={sunlockHealth.sunlockStatus?.serialNumber}
                        sunlockLastPingDate={sunlockHealth.sunlockStatus?.lastPingDate}
                        sunlockLogs={sunlockHealth?.sunlockLogsHistory || []}
                        internetConnection={sunlockHealth?.internetConnection}
                        onFixSunlockConnectionClick={null}
                      />
                    )}
                  </SrBox>
                )}

                {codeboxLock && (
                  <>
                    <SrBox margin="20px 0">
                      <SrFlex direction="col" align="center" styleName="codeboxCode">
                        <SrText font="greycliff" weight="700">
                          CodeBox Access
                        </SrText>
                        <SrBox height="8px" />
                        {!codeboxLockCode ? (
                          <SrBox padding="8px">
                            <SrButton
                              theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                              onClick={requestSelfShowingLockUnlock}
                              disabled={unlockButtonText === loadingButtonText}
                            >
                              {unlockButtonText}
                            </SrButton>
                          </SrBox>
                        ) : (
                          <SrText font="greycliff" weight="800" size="28">
                            {codeboxLockCode}&nbsp;(one-time use)
                          </SrText>
                        )}
                      </SrFlex>
                      {unlockError && (
                        <SrBox>
                          <SrErrorMessage>{unlockError}</SrErrorMessage>
                        </SrBox>
                      )}
                    </SrBox>
                    <SrBox textAlign="center" marginBottom="20px">
                      <SrText weight="600" fontSize="18px" align="center" font="greycliff">
                        If the code does not work, have the renter confirm the Serial Number on the
                        Codebox
                      </SrText>
                    </SrBox>
                    <div styleName="codeboxSerialNumberBox">
                      <span styleName="title">Serial Number</span>
                      <br />
                      <span styleName="serialNumber">{codeboxLock.serialNumber}</span>
                    </div>
                    <SrBox textAlign="center" padding="8px 0 16px">
                      <SrLink to="/admin/codebox" target="_blank">
                        <span className="blue-link">Reassign Codebox</span>
                      </SrLink>
                    </SrBox>
                  </>
                )}

                <SrText font="greycliff" weight="700" lineHeight="22px">
                  Renter Contact
                </SrText>
                <SrFlex direction="col" marginBottom="32px">
                  <SrText font="greycliff" weight="800" size="21" lineHeight="28px">
                    {user.name}
                  </SrText>
                  {user.phone && (
                    <SrText font="greycliff" weight="600" size="18" lineHeight="28px">
                      {formatPhoneNumber(user.phone)}
                    </SrText>
                  )}
                  {user.email && (
                    <SrText font="greycliff" weight="600" size="18" lineHeight="28px">
                      {user.email}
                    </SrText>
                  )}
                </SrFlex>
                <SrFlex direction="col">
                  <SrText font="greycliff" weight="800" size="18" lineHeight="24px" align="center">
                    Did the renter successfully tour the listing?
                  </SrText>
                </SrFlex>
                {formFields.map((formField) => (
                  <SrFormikField
                    formField={formField}
                    formikBag={formikBag}
                    theme={SR_FIELD_THEME_ADOBE}
                    key={formField.id}
                  />
                ))}
                <SrBox height="30px" />
                <SrButton theme={SR_BUTTON_THEME_ADOBE_GREEN} type="submit">
                  Submit
                </SrButton>
              </div>
            </Form>
          );
        }}
      />
    </SrModalV2>
  );
};

SrTourListingFailedModal.propTypes = {
  user: PropTypes.object.isRequired,
  listing: PropTypes.object.isRequired,
  sunlock: PropTypes.object,
  codeboxLock: PropTypes.object,
  tourListingId: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SrTourListingFailedModal.displayName = 'SrTourListingFailedModal';

export default SrTourListingFailedModal;
